import { useGetProductsQuery } from 'Services/ProductManagement/Features/Product/productApi'
import type { FetchProductListParams } from 'Services/ProductManagement/Features/Product/types'
import { getProductsInTariffParamKey } from 'Services/ProductManagement/Features/Product/utils'
import type { FetchListParams } from 'Services/ProductManagement/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

interface UseProductsWithParamsProps {
  hasNoTariff?: boolean
  inTariff?: string
  inCatalog?: string
}

export const SELECTED_PRODUCT_PARAMETER_KEY = 'selectedProduct'
const defaultSortByKey = 'productCode'
const defaultSortOrder = SORT_ORDER_ASC
const defaultCount = ROWS_PER_PAGE_DEFAULT

function useProductsWithParams({ hasNoTariff, inTariff, inCatalog }: UseProductsWithParamsProps) {
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchQueryParams, setSearchQueryParams] = useState<FetchListParams>({
    filter: searchParams.get('filter') ?? undefined,
    search_text: searchParams.get('search_text') ?? undefined,
    sort_by: searchParams.get('sort_by') ?? defaultSortByKey,
    sort_order: searchParams.get('sort_order') ?? defaultSortOrder,
    start_index: searchParams.get('start_index') ?? undefined,
    count: defaultCount
  })

  const params: FetchProductListParams = {
    ...searchQueryParams,
    has_no_tariff: hasNoTariff,
    [getProductsInTariffParamKey(inCatalog)]: inTariff,
    in_catalog: inCatalog
  }

  const {
    data: { resources: products } = { resources: [] },
    data: { total_results: totalResults } = { total_results: 0 },
    isFetching
  } = useGetProductsQuery(params)

  const selectedProductQueryParam = searchParams.get(SELECTED_PRODUCT_PARAMETER_KEY) ?? undefined
  const [selectedProductId, setSelectedProductId] = useState(selectedProductQueryParam)

  const setQueryParams = () => {
    Object.entries(searchQueryParams).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value.toString())
      } else {
        searchParams.delete(key)
      }
    })

    // Set selectedProductId if it exists
    selectedProductId && searchParams.set(SELECTED_PRODUCT_PARAMETER_KEY, selectedProductId)

    setSearchParams(searchParams)
  }

  const resetQueryParams = () => {
    setSearchQueryParams({
      filter: undefined,
      search_text: undefined,
      sort_by: defaultSortByKey,
      sort_order: defaultSortOrder,
      start_index: undefined,
      count: defaultCount
    })
  }

  useEffect(() => {
    setQueryParams()
  }, [searchQueryParams])

  useEffect(() => {
    if (selectedProductId) {
      setQueryParams()
    } else {
      searchParams.delete(SELECTED_PRODUCT_PARAMETER_KEY)
      setSearchParams(searchParams)
    }
  }, [selectedProductId])

  return {
    products,
    isFetching,
    searchQueryParams,
    setSearchQueryParams,
    totalResults,
    selectedProductId,
    setSelectedProductId,
    resetQueryParams
  }
}

export default useProductsWithParams
